/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, EyeOff } from 'react-feather';
import AuthService from '../../services/authService';
import icon from '../../assets/dark-iconp.png';

/**
 * Agent onboarding — the guided first-run setup an agent lands in from the link
 * we email them at account creation.
 *
 * Public route: no JWT. The raw token in the URL is the credential; the backend
 * hashes it and looks the agent up by hash. It's one-shot — burned the moment
 * the password is set. Everything after happens in the portal.
 *
 * STYLING: deliberately self-contained. Every class is `lo-` prefixed and defined
 * in the <style> block below, so this page inherits NOTHING from App.css. The
 * earlier version reused .reset-form / .password-input-container and the eye
 * toggles escaped their inputs because .password-input-container only gets
 * `position: relative` under .login-form in App.css, not .reset-form. (Worth
 * knowing: your reset-password view in App.js likely has that same bug.)
 * Font family is intentionally NOT set on body text — it inherits your stack.
 * Only the display heading opts into a serif.
 */
const AgentOnboarding = ({ onComplete }) => {
  const { token } = useParams();

  const [isValidating, setIsValidating] = useState(true);
  const [session, setSession] = useState(null);
  const [tokenError, setTokenError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const validate = async () => {
      try {
        const data = await AuthService.getOnboardingSession(token);
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) setTokenError(err.message);
      } finally {
        if (!cancelled) setIsValidating(false);
      }
    };

    validate();
    return () => { cancelled = true; };
  }, [token]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setFormError('');

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await AuthService.setOnboardingPassword(token, password);
      onComplete(data.user);
    } catch (err) {
      setFormError(err.message);
      setIsSubmitting(false);
    }
  };

  // App icon + ambient glow. Animates in first; everything else follows.
  const mark = (
    <div className="lo-mark lo-in" style={{ animationDelay: '0ms' }}>
      <div className="lo-mark-glow" />
      <img src={icon} alt="Lauft" className="lo-mark-img" />
    </div>
  );

  const shell = (children) => (
    <div className="lo-page">
      <style>{css}</style>
      <div className="lo-stage">
        {mark}
        {children}
      </div>
    </div>
  );

  // ---- Validating ----
  if (isValidating) {
    return shell(
      <div className="lo-in" style={{ animationDelay: '120ms' }}>
        <div className="lo-spinner" />
      </div>
    );
  }

  // ---- Dead link ----
  if (tokenError) {
    return shell(
      <>
        <h1 className="lo-title lo-in" style={{ animationDelay: '120ms' }}>
          This link isn't valid
        </h1>
        <p className="lo-subtitle lo-in" style={{ animationDelay: '200ms' }}>
          {tokenError}
        </p>
        <div className="lo-form lo-in" style={{ animationDelay: '280ms' }}>
          <button
            type="button"
            className="lo-button"
            onClick={() => { window.location.href = '/'; }}
          >
            Go to sign in
          </button>
        </div>
      </>
    );
  }

  // ---- Step 1: password ----
  if (session?.nextStep === 'password') {
    return shell(
      <>
        <h1 className="lo-title lo-in" style={{ animationDelay: '120ms' }}>
          Set your password
        </h1>
        <p className="lo-subtitle lo-in" style={{ animationDelay: '200ms' }}>
          Create a password for <span className="lo-email">{session.email}</span> to
          finish setting up your account.
        </p>

        <form className="lo-form" onSubmit={handleSetPassword}>
          <div className="lo-field lo-in" style={{ animationDelay: '300ms' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="lo-input"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength="6"
              required
              autoFocus
              autoComplete="new-password"
            />
            <button
              type="button"
              className="lo-eye"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          <div className="lo-field lo-in" style={{ animationDelay: '360ms' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              className="lo-input"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength="6"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="lo-eye"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          {formError && (
            <div className="lo-error">{formError}</div>
          )}

          <button
            type="submit"
            className="lo-button lo-in"
            style={{ animationDelay: '440ms' }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Setting password…' : 'Set password'}
          </button>

          <p className="lo-hint lo-in" style={{ animationDelay: '520ms' }}>
            At least 6 characters. You'll use this to sign in from now on.
          </p>
        </form>
      </>
    );
  }

  // ---- Fallback: a step this client doesn't know about ----
  return shell(
    <>
      <h1 className="lo-title lo-in" style={{ animationDelay: '120ms' }}>
        You're all set
      </h1>
      <p className="lo-subtitle lo-in" style={{ animationDelay: '200ms' }}>
        Your account setup is complete.
      </p>
      <div className="lo-form lo-in" style={{ animationDelay: '280ms' }}>
        <button
          type="button"
          className="lo-button"
          onClick={() => { window.location.href = '/'; }}
        >
          Go to sign in
        </button>
      </div>
    </>
  );
};

/**
 * Scoped styles. `lo-` prefix = lauft-onboarding. Nothing here leaks, nothing
 * here inherits.
 */
const css = `
  .lo-page {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    overflow-y: auto;
    background:
      radial-gradient(1100px 700px at 12% -8%, rgba(56, 68, 128, 0.30), transparent 60%),
      radial-gradient(900px 600px at 88% 108%, rgba(30, 42, 88, 0.26), transparent 62%),
      #0a0a0c;
  }

  .lo-stage {
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  /* ---- Icon tile ---- */
  .lo-mark {
    position: relative;
    margin-bottom: 34px;
  }
  .lo-mark-glow {
    position: absolute;
    inset: -26px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(99, 112, 190, 0.32), transparent 68%);
    filter: blur(14px);
    pointer-events: none;
  }
  .lo-mark-img {
    position: relative;
    display: block;
    width: 78px;
    height: 78px;
    border-radius: 20px;
    box-shadow: 0 10px 34px rgba(0, 0, 0, 0.55);
  }

  /* ---- Type ---- */
  /* No font-family here on purpose — inherits the portal's stack, same as
     everything else on this page. */
  .lo-title {
    margin: 0 0 12px 0;
    font-size: 1.9rem;
    font-weight: 700;
    letter-spacing: -0.015em;
    line-height: 1.2;
    color: #ffffff;
  }
  .lo-subtitle {
    margin: 0 0 34px 0;
    font-size: 0.97rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.52);
  }
  .lo-email {
    color: rgba(255, 255, 255, 0.82);
    white-space: nowrap;
  }

  /* ---- Form ---- */
  .lo-form {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* position:relative lives HERE, so the eye can never escape its input */
  .lo-field {
    position: relative;
    width: 100%;
    margin-bottom: 18px;
  }

  .lo-input {
    width: 100%;
    box-sizing: border-box;
    padding: 13px 44px 13px 2px;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.13);
    border-radius: 0;
    color: #ffffff;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.22s ease, box-shadow 0.22s ease;
  }
  .lo-input::placeholder {
    color: rgba(255, 255, 255, 0.26);
  }
  .lo-input:focus {
    border-bottom-color: rgba(255, 255, 255, 0.55);
    box-shadow: 0 1px 0 0 rgba(255, 255, 255, 0.18);
  }
  /* Chrome autofill repaints the field white — force it back */
  .lo-input:-webkit-autofill,
  .lo-input:-webkit-autofill:focus {
    -webkit-text-fill-color: #ffffff;
    -webkit-box-shadow: 0 0 0 1000px #0a0a0c inset;
    caret-color: #ffffff;
  }

  .lo-eye {
    position: absolute;
    top: 50%;
    right: 2px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.35);
    cursor: pointer;
    transition: color 0.18s ease;
  }
  .lo-eye:hover {
    color: rgba(255, 255, 255, 0.75);
  }

  .lo-button {
    width: 100%;
    margin-top: 10px;
    padding: 14px;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.12s ease;
  }
  .lo-button:hover:not(:disabled) {
    background: #ffffff;
    border-color: #ffffff;
    color: #0a0a0c;
  }
  .lo-button:active:not(:disabled) {
    transform: translateY(1px);
  }
  .lo-button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .lo-button:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 2px;
  }

  .lo-hint {
    margin: 18px 0 0 0;
    font-size: 0.8rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.3);
  }

  .lo-error {
    margin-bottom: 4px;
    padding: 10px 12px;
    border-radius: 9px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.26);
    color: #f7a3a3;
    font-size: 0.84rem;
    text-align: left;
  }

  .lo-spinner {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.12);
    border-top-color: rgba(255, 255, 255, 0.7);
    animation: lo-spin 0.7s linear infinite;
  }
  @keyframes lo-spin { to { transform: rotate(360deg); } }

  /* ---- Staggered entrance: mark first, then the rest ---- */
  .lo-in {
    opacity: 0;
    animation: lo-rise 0.62s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }
  @keyframes lo-rise {
    from { opacity: 0; transform: translateY(11px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    .lo-in { animation: none; opacity: 1; }
    .lo-spinner { animation-duration: 1.6s; }
  }

  @media (max-width: 480px) {
    .lo-title { font-size: 1.8rem; }
    .lo-mark { margin-bottom: 28px; }
  }
`;

export default AgentOnboarding;